// SEO Helper functions for generating structured data and meta tags

interface HorseData {
  id: string;
  name: string;
  sex?: string;
  color?: string;
  birth_date?: string;
}

interface RaceData {
  id: string;
  race_name: string;
  distance: string;
  category: string;
  time: string;
  location: string;
  date: string;
  prize_money?: number;
}

interface JockeyData {
  id: string;
  name: string;
}

// Generate structured data for horse profiles
export const generateHorseStructuredData = (horse: HorseData, stats?: any) => {
  return {
    "@context": "https://schema.org",
    "@type": "Animal",
    "name": horse.name,
    "species": "Horse",
    "url": `https://horsebeat.cz/horses/${horse.id}`,
    "identifier": horse.id,
    ...(horse.sex && { "sex": horse.sex }),
    ...(horse.color && { "color": horse.color }),
    ...(horse.birth_date && { "birthDate": horse.birth_date }),
    ...(stats && {
      "additionalProperty": [
        {
          "@type": "PropertyValue",
          "name": "Celkový počet startů",
          "value": stats.totalRaces || 0
        },
        {
          "@type": "PropertyValue", 
          "name": "Počet vítězství",
          "value": stats.wins || 0
        },
        {
          "@type": "PropertyValue",
          "name": "Úspěšnost",
          "value": stats.winRate ? `${stats.winRate}%` : "0%"
        }
      ]
    })
  };
};

// Generate structured data for race events
export const generateRaceStructuredData = (race: RaceData) => {
  const startDate = new Date(`${race.date}T${race.time}`);
  
  return {
    "@context": "https://schema.org",
    "@type": "SportsEvent",
    "name": race.race_name,
    "description": `${race.category} dostih na vzdálenost ${race.distance}`,
    "startDate": startDate.toISOString(),
    "location": {
      "@type": "Place",
      "name": race.location,
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "CZ"
      }
    },
    "sport": "Horse Racing",
    "url": `https://horsebeat.cz/race/${race.id}`,
    ...(race.prize_money && {
      "offers": {
        "@type": "Offer",
        "price": race.prize_money,
        "priceCurrency": "CZK",
        "description": "Celková dotace dostihu"
      }
    }),
    "organizer": {
      "@type": "Organization",
      "name": "Dostihový spolek",
      "url": "https://horsebeat.cz"
    }
  };
};

// Generate structured data for jockey profiles
export const generateJockeyStructuredData = (jockey: JockeyData, stats?: any) => {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": jockey.name,
    "jobTitle": "Žokej",
    "url": `https://horsebeat.cz/jockeys/${jockey.id}`,
    "worksFor": {
      "@type": "Organization",
      "name": "Dostihový sport ČR"
    },
    ...(stats && {
      "additionalProperty": [
        {
          "@type": "PropertyValue",
          "name": "Celkový počet jízd",
          "value": stats.totalRides || 0
        },
        {
          "@type": "PropertyValue",
          "name": "Počet vítězství", 
          "value": stats.wins || 0
        },
        {
          "@type": "PropertyValue",
          "name": "Úspěšnost",
          "value": stats.winRate ? `${stats.winRate}%` : "0%"
        }
      ]
    })
  };
};

// Generate FAQ structured data
export const generateFAQStructuredData = () => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Jak začít s tipováním na dostihy?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Začněte registrací na našem webu, poté si projděte statistiky koní a žokejů. Doporučujeme začít s menšími sázkami a postupně se učit z výsledků."
        }
      },
      {
        "@type": "Question",
        "name": "Kde najdu aktuální kurzy na dostihy?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Aktuální kurzy ze všech hlavních sázkových kanceláří najdete v sekci Kurzy. Kurzy se aktualizují v reálném čase před začátkem dostihů."
        }
      },
      {
        "@type": "Question",
        "name": "Jak funguje fantasy liga dostihů?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Ve fantasy lize si vytváříte virtuální stáj, kupujete koně za virtuální peníze a získáváte body podle jejich výsledků v reálných dostizích."
        }
      },
      {
        "@type": "Question", 
        "name": "Jaké jsou nejdůležitější dostihy v České republice?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Mezi nejprestižnější patří Velká Pardubická, St. Leger, České Derby a Československé Derby. Tyto dostihy mají nejvyšší dotace a největší prestiž."
        }
      },
      {
        "@type": "Question",
        "name": "Kde se konají dostihy v České republice?",
        "acceptedAnswer": {
          "@type": "Answer", 
          "text": "Hlavní hipodrómy jsou v Pardubicích, Praze (Velká Chuchle), Mostu, Karlových Varech a dalších městech. Každý hipodróm má svou specialitu a tradici."
        }
      }
    ]
  };
};

// Generate organization structured data
export const generateOrganizationStructuredData = () => {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Horsebeat.cz",
    "url": "https://horsebeat.cz",
    "logo": "https://horsebeat.cz/src/assets/logo-horsebeat.png",
    "description": "Nejkomplexnější zdroj informací o českých dostizích, statistikách koní, žokejů a dostihových kurzech.",
    "areaServed": "CZ",
    "foundingDate": "2024",
    "sameAs": [
      "https://www.facebook.com/horsebeat",
      "https://www.instagram.com/horsebeat"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "availableLanguage": "Czech"
    },
    "service": [
      {
        "@type": "Service",
        "name": "Dostihové statistiky",
        "description": "Kompletní databáze statistik koní a žokejů"
      },
      {
        "@type": "Service", 
        "name": "Kurzy a analýzy",
        "description": "Aktuální kurzy a odborné analýzy dostihů"
      },
      {
        "@type": "Service",
        "name": "Fantasy liga", 
        "description": "Virtuální dostihová liga pro fanoušky"
      }
    ]
  };
};

// Generate local business data for racetracks
export const generateRacetrackStructuredData = (name: string, city: string) => {
  const addressMap: Record<string, any> = {
    "Pardubice": {
      streetAddress: "Dostihová 101",
      postalCode: "530 02"
    },
    "Praha": {
      streetAddress: "Radotínská 69",
      postalCode: "159 00"
    },
    "Most": {
      streetAddress: "Hipodrom Most",
      postalCode: "434 01"
    }
  };

  return {
    "@context": "https://schema.org",
    "@type": "SportsActivityLocation",
    "name": `Hipodrom ${name}`,
    "description": `Dostihový hipodrom v ${city} s pravidelným programem galopových a překážkových dostihů.`,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": city,
      "addressCountry": "CZ",
      ...addressMap[city]
    },
    "url": `https://horsebeat.cz/racetrack/${city.toLowerCase()}`,
    "sport": "Horse Racing",
    "geo": {
      "@type": "GeoCoordinates",
      // Add coordinates if available
    }
  };
};

// SEO-friendly URL slug generator
export const generateSeoSlug = (text: string): string => {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .trim()
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-'); // Remove multiple hyphens
};

// Generate meta description for different content types
export const generateMetaDescription = (
  type: 'horse' | 'jockey' | 'race' | 'general' | 'racetrack',
  data?: any
): string => {
  switch (type) {
    case 'horse':
      return `Kompletní profil koně ${data?.name}. Statistiky, výsledky dostihů, úspěšnost a aktuální kurzy. Sledujte výkony tohoto koně na Horsebeat.cz.`;
    
    case 'jockey':
      return `Profil žokeje ${data?.name}. Statistiky jízd, úspěšnost, vítězství a aktuální výsledky. Kompletní přehled kariéry na Horsebeat.cz.`;
    
    case 'race':
      return `Dostih ${data?.name} - ${data?.location}. Aktuální kurzy, startovní pole, analýzy a tipy. Sledujte live výsledky na Horsebeat.cz.`;
    
    case 'racetrack':
      return `Hipodrom ${data?.name} - kompletní přehled dostihů, výsledků a programu. Aktuální informace o dostizích v ${data?.city} na Horsebeat.cz.`;
    
    default:
      return "Dostihové syndikáty pro fanoušky i začátečníky. Podrobné statistiky koní a žokejů, kompletní databáze českých dostihů s aktuálními výsledky.";
  }
};